import { ChangeEvent, MouseEvent, useEffect, useRef, useState } from 'react';
import {Input} from '@shortsworks/ui/index';

interface AnglePickerProps {
  angle: number;
  onChange: (angle: number) => void;
}

const radius = 14;

const calcX2 = (angle: number) => 15 + radius * Math.cos(((angle - 90) * Math.PI) / 180);
const calcY2 = (angle: number) => 15 + radius * Math.sin(((angle - 90) * Math.PI) / 180);

export function AnglePicker({ angle: initialAngle = 0, onChange }: AnglePickerProps) {
  const svgRef = useRef<SVGSVGElement | null>(null);
  const [ x2, setX2 ] = useState<number>(0);
  const [ y2, setY2 ] = useState<number>(0);
  const [ angle, setAngle ] = useState<number>(initialAngle);

  useEffect(() => {
    if (svgRef.current) {
      setX2(calcX2(angle));
      setY2(calcY2(angle));
    }
  }, []);

  const handleMouseDown = (e: MouseEvent) => {
    updateAngle(e);

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }

  const handleMouseMove = (e: globalThis.MouseEvent) => {
    updateAngle(e);
  }

  const handleMouseUp = () => {
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
  }

  const updateAngle = (e: MouseEvent | globalThis.MouseEvent) => {
    const rect = svgRef.current?.getBoundingClientRect();
    const x = e.clientX - rect!.left - radius;
    const y = e.clientY - rect!.top - radius;

    const angle = Math.round((Math.atan2(y, x) * 180) / Math.PI + 450) % 360 + 1;
    setAngle(angle);
    setX2(calcX2(angle));
    setY2(calcY2(angle));

    onChange(angle);
  }

  const handleAngleInput = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value === '' ? 0 : parseInt(e.target.value, 10);
    const angle = Math.min(360, Math.max(0, value)) % 360;
    setAngle( angle);
    setX2(calcX2(angle));
    setY2(calcY2(angle));

    onChange(angle);
  }

  return (
    <div className="relative flex items-center gap-[10px]">
      <svg ref={svgRef} className="cursor-pointer" width="30" height="30" onMouseDown={handleMouseDown} onClick={updateAngle}>
        <circle cx="15" cy="15" r="14" fill="transparent" stroke="#D9D9D9" strokeWidth="1" />
        <line x1="15" y1="15" x2={x2} y2={y2} stroke="#D9D9D9" strokeWidth="1" />
        <circle cx="15" cy="15" r="1.5" fill="white" stroke="#D9D9D9" strokeWidth="1" />
      </svg>
      <div className="angle-input-wrapper">
        <Input type="number" className="h-[30px] text-[12px]" value={angle} min="0" max="360" onInput={handleAngleInput} />
      </div>
    </div>
  );
}
